"use client";

import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import clsx from "clsx";
import {Button} from "@/components/ui/button";
import {Progress} from "@/components/ui/progress";
import Icon, {ICON_TYPE} from "@/components/library/Icon";
import type {MemoryImportResult} from "@/hooks/useGailGptMemories";

/** Mirrors MAX_IMPORT_CHARS in the import API route. */
const MAX_IMPORT_CHARS = 20_000;
const STAGE_ROTATE_MS = 2400;

const STAGE_KEYS = [
   "memory.importStageAnalyzing",
   "memory.importStageExtracting",
   "memory.importStageSaving",
] as const;

type MemoryImportPanelProps = Readonly<{
   importing: boolean;
   disabled?: boolean;
   onImport: (text: string) => Promise<MemoryImportResult | null>;
   onCancel: () => void;
}>;

function ImportResultBanner({
   result,
   onDismiss,
}: Readonly<{
   result: MemoryImportResult;
   onDismiss: () => void;
}>) {
   const {t} = useTranslation("ask");
   const tone =
      result.extracted === 0
         ? "empty"
         : result.failed.length > 0
           ? "partial"
           : "success";
   return (
      <div
         className={clsx("animate-fade-in flex flex-col gap-2 rounded-lg border px-3 py-2", {
            "border-200 bg-200/50": tone === "empty",
            "border-green-600/30 bg-green-500/10": tone === "success",
            "border-amber-600/30 bg-amber-500/10": tone === "partial",
         })}
         role="status"
      >
         <div className="flex items-start justify-between gap-2">
            <p
               className={clsx("flex items-center gap-2 text-sm", {
                  "text-600": tone === "empty",
                  "text-green-600 dark:text-green-400": tone === "success",
                  "text-amber-600 dark:text-amber-400": tone === "partial",
               })}
            >
               <Icon
                  icon={
                     tone === "success"
                        ? "circle-check"
                        : tone === "partial"
                          ? "triangle-exclamation"
                          : "circle-info"
                  }
                  type={ICON_TYPE.LIGHT}
                  className="shrink-0"
               />
               {tone === "empty" && t("memory.importEmpty")}
               {tone === "success" && t("memory.importSuccess", {count: result.saved.length})}
               {tone === "partial" &&
                  t("memory.importPartial", {saved: result.saved.length, total: result.extracted})}
            </p>
            <button
               type="button"
               onClick={onDismiss}
               aria-label={t("memory.dismiss")}
               className="text-500 hover:text-800 shrink-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-300"
            >
               <Icon icon="xmark" type={ICON_TYPE.LIGHT} />
            </button>
         </div>
         {tone === "partial" && (
            <ul className="flex flex-col gap-1">
               {result.failed.map((failure) => (
                  <li
                     key={failure.text}
                     className="border-200 bg-panel flex flex-col gap-0.5 rounded-md border px-2 py-1.5"
                  >
                     <span className="text-800 text-xs whitespace-pre-wrap">{failure.text}</span>
                     <span className="text-500 text-xs">{t(failure.error)}</span>
                  </li>
               ))}
            </ul>
         )}
      </div>
   );
}

export default function MemoryImportPanel({
   importing,
   disabled = false,
   onImport,
   onCancel,
}: MemoryImportPanelProps) {
   const {t, i18n} = useTranslation("ask");
   const [draft, setDraft] = useState("");
   const [result, setResult] = useState<MemoryImportResult | null>(null);
   const [stage, setStage] = useState(0);

   useEffect(() => {
      if (!importing) {
         setStage(0);
         return;
      }
      const timer = setInterval(() => {
         setStage((current) => (current + 1) % STAGE_KEYS.length);
      }, STAGE_ROTATE_MS);
      return () => clearInterval(timer);
   }, [importing]);

   const trimmed = draft.trim();
   const tooLong = draft.length > MAX_IMPORT_CHARS;
   const busy = importing || disabled;

   const handleImport = async (event: {preventDefault: () => void}) => {
      event.preventDefault();
      if (!trimmed || tooLong || busy) return;
      setResult(null);
      const outcome = await onImport(trimmed);
      if (!outcome) return; // the hook surfaces the error banner
      setResult(outcome);
      if (outcome.saved.length > 0) setDraft("");
   };

   return (
      <div className="border-200 bg-100/50 flex flex-col gap-3 rounded-xl border p-3">
         <p className="text-500 flex items-start gap-2 text-xs">
            <Icon icon="sparkles" type={ICON_TYPE.LIGHT} className="mt-0.5 shrink-0" />
            <span>{t("memory.importHint")}</span>
         </p>

         <form className="flex flex-col gap-2" onSubmit={(event) => void handleImport(event)}>
            <textarea
               value={draft}
               onChange={(event) => setDraft(event.target.value)}
               placeholder={t("memory.importPlaceholder")}
               aria-label={t("memory.importAriaLabel")}
               disabled={busy}
               rows={5}
               className="border-input bg-transparent w-full rounded-md border px-3 py-2 text-sm"
            />
               <div className="flex items-center justify-between gap-2">
                  <span
                     className={clsx("text-xs", tooLong ? "text-red-600 dark:text-red-400" : "text-500")}
                  >
                     {tooLong
                        ? t("memory.importTooLong", {max: MAX_IMPORT_CHARS.toLocaleString(i18n.language)})
                        : `${draft.length.toLocaleString(i18n.language)} / ${MAX_IMPORT_CHARS.toLocaleString(i18n.language)}`}
                  </span>
                  <div className="flex gap-2">
                     <Button type="button" variant="ghost" size="sm" disabled={importing} onClick={onCancel}>
                        {t("memory.cancel")}
                     </Button>
                     <Button type="submit" size="sm" disabled={busy || !trimmed || tooLong}>
                        {importing ? (
                           <>
                              <Icon icon="spinner" type={ICON_TYPE.LIGHT} className="animate-spin" />
                              {t("memory.importing")}
                           </>
                        ) : (
                           <>
                              <Icon icon="wand-magic-sparkles" type={ICON_TYPE.LIGHT} />
                              {t("memory.importButton")}
                           </>
                        )}
                     </Button>
                  </div>
               </div>
         </form>

         {importing && (
            <div className="flex flex-col gap-1.5" aria-live="polite">
               <Progress isIndeterminate size="sm" />
               <p className="text-500 animate-pulse text-xs">{t(STAGE_KEYS[stage])}</p>
            </div>
         )}

         {result && !importing && (
            <ImportResultBanner result={result} onDismiss={() => setResult(null)} />
         )}
      </div>
   );
}
